import React from 'react';
import api from '../api/axios';
import { localizeApiError, t } from '../i18n';
import './DailyMissionManagement.css';

const ACTION_OPTIONS = [
  { value: 'WIN_COUNT', label: '승리 횟수', targetLabel: '승리 횟수', placeholder: '예: 5', step: '1' },
  { value: 'ASSET_TARGET', label: '자산 금액 달성', targetLabel: '목표 Asset', placeholder: '예: 1000', step: '0.01' },
  { value: 'RECHARGE_AMOUNT', label: '충전 (Deposit)', targetLabel: '누적 실제 입금액', placeholder: '예: 500', step: '0.01' },
  { value: 'BET_AMOUNT', label: '베팅금액 달성', targetLabel: '누적 베팅 금액', placeholder: '예: 2500', step: '0.01' },
  { value: 'SHOWDOWN_COUNT', label: '쇼다운 도달 횟수', targetLabel: '리버 쇼다운 횟수', placeholder: '예: 10', step: '1' },
];

function normalizeBindingKey(value, fallback = 'mission') {
  const key = String(value || '').trim().toLowerCase().replace(/[^a-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48);
  return key || fallback;
}

function groupTitle(group) {
  const code = String(group?.display_code || '').trim();
  const name = group?.name;
  let label = '';
  if (name && typeof name === 'object') label = name.ko || name.en || Object.values(name)[0] || '';
  else label = name || '';
  return label && label !== code ? `${label} · ${code}` : (label || code);
}

function autoBindingKey(group, index = 0) {
  const title = groupTitle(group);
  const nlMatch = title.match(/\bNL\s*([0-9]+(?:\.[0-9]+)?)\b/i);
  if (nlMatch) return normalizeBindingKey(`nl${nlMatch[1].replace('.', '_')}`, `mission_${index + 1}`);
  return normalizeBindingKey(group?.name || group?.display_code, `mission_${index + 1}`);
}

const newCondition = (displayCode = '', bindingKey = 'mission') => ({
  display_code: displayCode,
  display_codes: displayCode ? [displayCode] : [],
  binding_key: bindingKey,
  type: 'WIN_COUNT',
  target: '',
  reward_amount: '',
});

function actionMeta(type) {
  return ACTION_OPTIONS.find((item) => item.value === type) || ACTION_OPTIONS[0];
}

export default function DailyMissionManagement() {
  const [groups, setGroups] = React.useState([]);
  const [active, setActive] = React.useState(null);
  const [resetHours, setResetHours] = React.useState('24');
  const [conditions, setConditions] = React.useState([newCondition('')]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState(null);

  const firstDisplayCode = groups[0]?.display_code || '';

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setMessage(null);

      // Table groups and the active mission are independent resources.
      // A missing/new daily_mission_events table must never make the NL selector empty.
      const [groupResult, missionResult] = await Promise.allSettled([
        api.get('/admin/daily-mission/table-groups'),
        api.get('/admin/daily-mission'),
      ]);
      if (cancelled) return;

      const groupRes = groupResult.status === 'fulfilled' ? groupResult.value : null;
      const rows = Array.isArray(groupRes?.data?.items) ? groupRes.data.items : [];
      setGroups(rows);

      const missionRes = missionResult.status === 'fulfilled' ? missionResult.value : null;
      const current = missionRes?.data?.active || null;
      setActive(current);

      if (current) {
        setResetHours(String(current.reset_interval_hours ?? 24));
        setConditions(Array.isArray(current.conditions) && current.conditions.length
          ? current.conditions.map((condition, index) => ({
              display_code: condition.display_code || '',
              display_codes: Array.isArray(condition.display_codes) && condition.display_codes.length
                ? condition.display_codes
                : (condition.display_code ? [condition.display_code] : []),
              binding_key: condition.binding_key || normalizeBindingKey(condition.display_code, `mission_${index + 1}`),
              type: condition.type || 'WIN_COUNT',
              target: String(condition.target ?? ''),
              reward_amount: String(condition.reward_amount ?? current.reward_amount ?? ''),
            }))
          : [newCondition(rows[0]?.display_code || '', autoBindingKey(rows[0], 0))]);
      } else {
        setConditions([newCondition(rows[0]?.display_code || '', autoBindingKey(rows[0], 0))]);
      }

      const errors = [];
      if (groupResult.status === 'rejected') {
        const error = groupResult.reason;
        errors.push(localizeApiError(error, '테이블 그룹 목록을 불러오지 못했습니다.'));
      } else if (!rows.length) {
        errors.push(t('등록된 테이블 그룹이 없습니다. 테이블 관리에서 그룹을 먼저 확인해 주세요.'));
      }
      if (missionResult.status === 'rejected') {
        const error = missionResult.reason;
        errors.push(localizeApiError(error, '오늘의 미션 설정을 불러오지 못했습니다.'));
      }
      if (errors.length) setMessage({ type: 'error', text: errors.join(' / ') });

      setLoading(false);
    })().catch((error) => {
      if (!cancelled) {
        setLoading(false);
        setMessage({ type: 'error', text: localizeApiError(error, '오늘의 미션 설정을 불러오지 못했습니다.') });
      }
    });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    if (!firstDisplayCode) return;
    setConditions((prev) => prev.map((condition) => {
      if (condition.type === 'RECHARGE_AMOUNT') return { ...condition, display_code: 'GLOBAL', display_codes: ['GLOBAL'] };
      if (Array.isArray(condition.display_codes) && condition.display_codes.length) return condition;
      const firstGroup = groups.find((group) => group.display_code === firstDisplayCode);
      return { ...condition, display_code: firstDisplayCode, display_codes: [firstDisplayCode], binding_key: autoBindingKey(firstGroup, 0) };
    }));
  }, [firstDisplayCode]);

  const updateCondition = (index, patch) => {
    setConditions((prev) => prev.map((condition, i) => i === index ? { ...condition, ...patch } : condition));
  };

  const addCondition = () => {
    setConditions((prev) => {
      const group = groups.find((item) => item.display_code === firstDisplayCode);
      let key = autoBindingKey(group, prev.length);
      const used = new Set(prev.map((item) => item.binding_key));
      let suffix = 2;
      const base = key;
      while (used.has(key)) key = normalizeBindingKey(`${base}_${suffix++}`, `mission_${prev.length + 1}`);
      return [...prev, newCondition(firstDisplayCode, key)];
    });
  };

  const removeCondition = (index) => {
    setConditions((prev) => prev.length <= 1 ? prev : prev.filter((_, i) => i !== index));
  };

  const validate = () => {
    const hours = Number(resetHours);
    if (!Number.isInteger(hours) || hours < 1 || hours > 720) return t('초기화 시간은 1~720시간의 정수로 입력해 주세요.');
    if (!conditions.length) return t('달성 조건을 1개 이상 추가해 주세요.');
    for (let i = 0; i < conditions.length; i += 1) {
      const condition = conditions[i];
      if (condition.type !== 'RECHARGE_AMOUNT' && (!Array.isArray(condition.display_codes) || !condition.display_codes.length)) return t('달성 조건 #{number}의 테이블 그룹을 1개 이상 선택해 주세요.', { number: i + 1 });
      if (!ACTION_OPTIONS.some((option) => option.value === condition.type)) return t('달성 조건 #{number}의 행동이 올바르지 않습니다.', { number: i + 1 });
      const target = Number(condition.target);
      if (!Number.isFinite(target) || target <= 0) return t('달성 조건 #{number}의 목표값을 입력해 주세요.', { number: i + 1 });
      if (['WIN_COUNT', 'SHOWDOWN_COUNT'].includes(condition.type) && !Number.isInteger(target)) return t('달성 조건 #{number}의 횟수는 정수로 입력해 주세요.', { number: i + 1 });
      const reward = Number(condition.reward_amount);
      if (!Number.isFinite(reward) || reward <= 0) return t('달성 조건 #{number}의 보상 금액을 0보다 크게 입력해 주세요.', { number: i + 1 });
    }
    return '';
  };

  const save = async () => {
    const errorText = validate();
    if (errorText) {
      setMessage({ type: 'error', text: errorText });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const response = await api.post('/admin/daily-mission', {
        reset_interval_hours: Number(resetHours),
        reward_amount: conditions.reduce((sum, condition) => sum + Number(condition.reward_amount || 0), 0),
        conditions: conditions.map((condition, index) => ({
          display_code: condition.type === 'RECHARGE_AMOUNT' ? 'GLOBAL' : condition.display_codes[0],
          display_codes: condition.type === 'RECHARGE_AMOUNT' ? ['GLOBAL'] : condition.display_codes,
          binding_key: normalizeBindingKey(condition.binding_key, `mission_${index + 1}`),
          type: condition.type,
          target: Number(condition.target),
          reward_amount: Number(condition.reward_amount),
        })),
      });
      const created = response?.data?.active || null;
      setActive(created);
      setMessage({
        type: response?.data?.redis_synced === false ? 'warn' : 'success',
        text: response?.data?.warning || t('오늘의 미션을 저장했습니다. 새 이벤트 회차로 진행도가 분리됩니다.'),
      });
    } catch (error) {
      setMessage({ type: 'error', text: localizeApiError(error, '오늘의 미션 저장에 실패했습니다.') });
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async () => {
    if (!active) return;
    if (!window.confirm(t('현재 오늘의 미션을 중지하시겠습니까?'))) return;
    try {
      await api.post('/admin/daily-mission/deactivate');
      setActive(null);
      setMessage({ type: 'success', text: t('오늘의 미션을 중지했습니다.') });
    } catch (error) {
      setMessage({ type: 'error', text: localizeApiError(error, '이벤트 중지에 실패했습니다.') });
    }
  };

  const copyUrl = async () => {
    if (!active?.public_url) return;
    try {
      await navigator.clipboard.writeText(active.public_url);
      setMessage({ type: 'success', text: t('팝업 URL을 복사했습니다.') });
    } catch (_) {
      window.prompt(t('아래 URL을 복사하세요.'), active.public_url);
    }
  };

  const totalRewardAmount = conditions.reduce((sum, condition) => sum + (Number(condition.reward_amount) || 0), 0);

  if (loading) {
    return <section className="daily-mission-page"><div className="dm-admin-loading">{t('오늘의 미션 설정을 불러오는 중...')}</div></section>;
  }

  return (
    <section className="daily-mission-page">
      <div className="dm-page-heading">
        <div>
          <span>DAILY MISSION</span>
          <h1>{t('오늘의 미션 설정')}</h1>
          <p>{t('미션 조건과 보상만 설정합니다. 유저 팝업은 유저클라이언트에 최적화된 고정 디자인으로 표시됩니다.')}</p>
        </div>
        <div className={`dm-page-status ${active ? 'is-live' : ''}`}>
          <b>{active ? 'ACTIVE' : 'OFF'}</b>
          <span>{active ? `${active.reset_interval_hours}H RESET` : t('등록된 이벤트 없음')}</span>
        </div>
      </div>

      {message && <div className={`dm-admin-message is-${message.type}`}>{message.text}</div>}

      <div className="dm-admin-grid two-columns">
        <label className="dm-admin-field">
          <span>{t('초기화 시간')}</span>
          <div className="dm-admin-input-with-unit">
            <input type="number" min="1" max="720" step="1" value={resetHours} onChange={(e) => setResetHours(e.target.value)} />
            <b>{t('시간마다')}</b>
          </div>
          <small>{t('저장 시점을 기준으로 설정 시간마다 Redis 진행도 회차가 자동 변경됩니다.')}</small>
        </label>

        <div className="dm-admin-field">
          <span>{t('전체 잠재 보상 합계')}</span>
          <div className="dm-admin-input-with-unit">
            <input type="number" value={totalRewardAmount} readOnly />
            <b>Asset</b>
          </div>
          <small>{t('각 달성 조건마다 아래에서 별도 보상을 설정합니다. 조건별 보상은 서로 독립적으로 수령됩니다.')}</small>
        </div>
      </div>

      <div className="dm-admin-panel">
        <div className="dm-admin-panel-head">
          <div><span>CONDITIONS</span><h2>{t('달성 조건')}</h2></div>
          <button type="button" className="dm-admin-add" onClick={addCondition}>{t('+ 달성 조건 추가')}</button>
        </div>

        <div className="dm-condition-editor-list">
          {conditions.map((condition, index) => {
            const meta = actionMeta(condition.type);
            const selectedCodes = Array.isArray(condition.display_codes) ? condition.display_codes : [];
            return (
              <div className="dm-condition-editor" key={`${index}-${condition.type}`}>
                <span className="dm-condition-number">{String(index + 1).padStart(2, '0')}</span>
                <label>
                  <span>{t('대상 / 테이블 그룹 (복수 선택)')}</span>
                  {condition.type === 'RECHARGE_AMOUNT' ? (
                    <select value="GLOBAL" disabled><option value="GLOBAL">{t('충전 (Deposit / NL 무관)')}</option></select>
                  ) : (
                    <select multiple value={selectedCodes} onChange={(e) => {
                      const nextCodes = Array.from(e.target.selectedOptions).map((option) => option.value).filter(Boolean);
                      const first = nextCodes[0] || '';
                      const nextGroup = groups.find((group) => group.display_code === first);
                      const used = new Set(conditions.filter((_, i) => i !== index).map((item) => item.binding_key));
                      let nextKey = condition.binding_key || autoBindingKey(nextGroup, index);
                      const base = nextKey;
                      let suffix = 2;
                      while (used.has(nextKey)) nextKey = normalizeBindingKey(`${base}_${suffix++}`, `mission_${index + 1}`);
                      updateCondition(index, { display_codes: nextCodes, display_code: first, binding_key: nextKey });
                    }} size={Math.min(6, Math.max(3, groups.length))}>
                      {groups.map((group) => <option key={group.display_code} value={group.display_code}>{groupTitle(group)}</option>)}
                    </select>
                  )}
                  <small>{t('Ctrl/⌘ 또는 Shift로 여러 그룹을 선택할 수 있습니다.')}</small>
                </label>
                <label>
                  <span>{t('행동')}</span>
                  <select value={condition.type} onChange={(e) => {
                    const nextType = e.target.value;
                    const nextCodes = nextType === 'RECHARGE_AMOUNT'
                      ? ['GLOBAL']
                      : (selectedCodes.filter((code) => code !== 'GLOBAL').length ? selectedCodes.filter((code) => code !== 'GLOBAL') : [firstDisplayCode].filter(Boolean));
                    const nextDisplayCode = nextCodes[0] || '';
                    const nextGroup = groups.find((group) => group.display_code === nextDisplayCode);
                    let nextKey = nextType === 'RECHARGE_AMOUNT' ? `deposit_${index + 1}` : autoBindingKey(nextGroup, index);
                    const used = new Set(conditions.filter((_, i) => i !== index).map((item) => item.binding_key));
                    const base = nextKey;
                    let suffix = 2;
                    while (used.has(nextKey)) nextKey = normalizeBindingKey(`${base}_${suffix++}`, `mission_${index + 1}`);
                    updateCondition(index, { type: nextType, target: '', display_codes: nextCodes, display_code: nextDisplayCode, binding_key: nextKey });
                  }}>
                    {ACTION_OPTIONS.map((option) => <option key={option.value} value={option.value}>{t(option.label)}</option>)}
                  </select>
                </label>
                <label>
                  <span>{t(meta.targetLabel)}</span>
                  <input type="number" min={meta.step === '1' ? '1' : '0.01'} step={meta.step} value={condition.target} onChange={(e) => updateCondition(index, { target: e.target.value })} placeholder={t(meta.placeholder)} />
                </label>
                <label>
                  <span>{t('개별 보상 금액')}</span>
                  <div className="dm-admin-input-with-unit">
                    <input type="number" min="0.01" step="0.01" value={condition.reward_amount} onChange={(e) => updateCondition(index, { reward_amount: e.target.value })} placeholder={t('예: 25')} />
                    <b>Asset</b>
                  </div>
                </label>
                <button type="button" className="dm-condition-remove" onClick={() => removeCondition(index)} disabled={conditions.length <= 1}>{t('삭제')}</button>
              </div>
            );
          })}
        </div>

        <div className="dm-condition-rules">
          <span>{t('집계 규칙')}</span>
          <p>{t('승리는 폴드승 포함 · 쇼다운은 실제 SHOWDOWN이면서 리버를 거친 경우만 인정 · 충전은 Cashier에서 FINAL_DONE으로 확정되어 CASHIER_DEPOSIT 원장에 기록된 실제 입금액만 인정(테이블 Buy-in/리바이 제외) · 베팅은 블라인드를 포함한 실제 핸드 총 투입액 기준입니다.')}</p>
        </div>
      </div>

      <div className="dm-admin-panel">
        <div className="dm-admin-panel-head">
          <div><span>USER POPUP</span><h2>{t('유저 팝업 표시')}</h2></div>
        </div>
        <div className="dm-condition-rules">
          <span>{t('고정 디자인')}</span>
          <p>{t('팝업 HTML/CSS/이미지 업로드 기능은 사용하지 않습니다. 위에서 등록한 달성 조건, 복수 테이블 그룹, 목표값, 조건별 보상 금액과 유저의 실시간 진행도가 유저클라이언트 고정 팝업에 자동 표시됩니다.')}</p>
        </div>
      </div>

      <div className="dm-admin-savebar">
        <div className="dm-generated-url">
          <span>{t('생성된 팝업 URL')}</span>
          {active?.public_url ? <><code>{active.public_url}</code><button type="button" onClick={copyUrl}>{t('복사')}</button></> : <em>{t('저장 후 자동 생성됩니다.')}</em>}
        </div>
        <div className="dm-save-actions">
          {active && <button type="button" className="dm-stop-button" onClick={deactivate}>{t('이벤트 중지')}</button>}
          <button type="button" className="dm-save-button" onClick={save} disabled={saving || !groups.length}>{saving ? t('저장 중...') : t('저장 / 새 회차 등록')}</button>
        </div>
      </div>
    </section>
  );
}
